import React, { useState, useRef, useEffect, useCallback } from 'react';
import './GoogleAdsChat.css';

const API_URL = process.env.REACT_APP_API_URL || '';

const DEFAULT_SUGGESTIONS = [
  "Quels sont les 3 problèmes les plus importants de ce compte ?",
  "Pourquoi me proposes-tu ces actions ?",
  "Passe la première exclusion en correspondance exacte.",
  "Limite l'augmentation de budget à +10 %.",
];

// Markdown léger (repris de AISearch.js)
function formatInline(text) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith('**') && part.endsWith('**') ? <strong key={i}>{part.slice(2, -2)}</strong> : part);
}
function formatMessage(text) {
  if (!text) return null;
  return text.split('\n').map((line, i) => {
    if (line.startsWith('### ')) return <h4 key={i} className="gac-md-h">{formatInline(line.slice(4))}</h4>;
    if (line.startsWith('## ')) return <h4 key={i} className="gac-md-h">{formatInline(line.slice(3))}</h4>;
    if (line.match(/^[-•*]\s/)) return <li key={i} className="gac-md-li">{formatInline(line.slice(2))}</li>;
    if (line.match(/^\d+\.\s/)) return <li key={i} className="gac-md-li">{formatInline(line.replace(/^\d+\.\s/, ''))}</li>;
    if (!line.trim()) return <br key={i} />;
    return <p key={i} className="gac-md-p">{formatInline(line)}</p>;
  });
}

const DEFAULT_TOOL_LABELS = {
  lister_propositions: '📋 Lecture des propositions…',
  detail_proposition: '🔎 Détail d\'une proposition…',
  ajouter_negatif: '🚫 Ajout d\'une exclusion…',
  modifier_negatif: '✏️ Modification d\'une exclusion…',
  ajuster_budget_proposition: '💶 Ajustement de budget…',
  retirer_proposition: '🗑️ Retrait d\'une proposition…',
  interroger_donnees: '📊 Lecture des données…',
};

// Panneau de chat IA réutilisable (Google Ads par défaut, paramétrable pour GA4).
export default function GoogleAdsChat({
  customer, idValue, idKey = 'customer', endpoint = '/api/google-ads/audit-chat/stream',
  accountName, onActionsChanged, suggestions, toolLabels, fabLabel = "💬 Discuter avec l'IA",
  disclaimer = "L'IA façonne les propositions — rien n'est appliqué sans ta validation.",
}) {
  const id = idValue ?? customer;
  const SUGGESTIONS = suggestions || DEFAULT_SUGGESTIONS;
  const TOOL_LABELS = toolLabels || DEFAULT_TOOL_LABELS;
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [streamContent, setStreamContent] = useState('');
  const [activeTool, setActiveTool] = useState(null);
  const endRef = useRef(null);

  // Réinitialise la conversation au changement de compte/propriété.
  useEffect(() => { setMessages([]); setStreamContent(''); }, [id]);
  const scrollDown = useCallback(() => endRef.current?.scrollIntoView({ behavior: 'smooth' }), []);
  useEffect(() => { if (open) scrollDown(); }, [messages, streamContent, open, scrollDown]);

  const send = async (text) => {
    const content = (text || input).trim();
    if (!content || streaming || !id) return;
    const next = [...messages, { role: 'user', content }];
    setMessages(next); setInput(''); setStreaming(true); setStreamContent(''); setActiveTool(null);
    const history = next.filter(m => m.role === 'user' || m.role === 'assistant').slice(-10);
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ [idKey]: id, message: content, history }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let acc = '', buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const s = line.slice(6).trim();
          if (!s) continue;
          let ev; try { ev = JSON.parse(s); } catch { continue; }
          if (ev.type === 'token') { acc += ev.content; setStreamContent(acc); }
          else if (ev.type === 'full') { acc = ev.content; setStreamContent(acc); }
          else if (ev.type === 'tool_start') setActiveTool(ev.name);
          else if (ev.type === 'tool_done') setActiveTool(null);
          else if (ev.type === 'actions_changed') { onActionsChanged && onActionsChanged(); }
          else if (ev.type === 'done') { const c = acc; if (c) setMessages(p => [...p, { role: 'assistant', content: c }]); setStreamContent(''); }
          else if (ev.type === 'error') { const c = ev.content || 'Erreur.'; setMessages(p => [...p, { role: 'assistant', content: c }]); setStreamContent(''); }
        }
      }
      if (acc) setMessages(p => (p[p.length - 1]?.content === acc ? p : [...p, { role: 'assistant', content: acc }]));
      setStreamContent('');
    } catch (e) {
      setMessages(p => [...p, { role: 'assistant', content: '❌ Erreur de connexion au chat.' }]);
      setStreamContent('');
    } finally { setStreaming(false); setActiveTool(null); }
  };

  const onKey = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } };

  if (!open) {
    return (
      <button className="gac-fab" onClick={() => setOpen(true)} title="Discuter avec l'IA de cet audit">
        {fabLabel}
      </button>
    );
  }

  return (
    <div className="gac-panel">
      <div className="gac-head">
        <div><strong>💬 Assistant audit</strong><span className="gac-head-sub">{accountName}</span></div>
        <button className="gac-close" onClick={() => setOpen(false)}>✕</button>
      </div>

      <div className="gac-body">
        {messages.length === 0 && !streamContent && (
          <div className="gac-intro">
            <p>Pose-moi des questions sur l'audit, challenge-moi, ou demande-moi d'ajuster les actions proposées (j'édite la file ; tu valides ensuite).</p>
            <div className="gac-suggestions">
              {SUGGESTIONS.map((s, i) => <button key={i} className="gac-chip" onClick={() => send(s)}>{s}</button>)}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`gac-msg gac-msg-${m.role}`}>
            <div className="gac-msg-content">{m.role === 'assistant' ? formatMessage(m.content) : m.content}</div>
          </div>
        ))}
        {(streamContent || streaming) && (
          <div className="gac-msg gac-msg-assistant">
            <div className="gac-msg-content">{streamContent ? formatMessage(streamContent) : null}{streaming && <span className="gac-cursor" />}</div>
            {activeTool && <div className="gac-tool">{TOOL_LABELS[activeTool] || `⏳ ${activeTool}…`}</div>}
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div className="gac-input">
        <textarea rows={1} placeholder="Votre message…" value={input}
          onChange={e => setInput(e.target.value)} onKeyDown={onKey} disabled={streaming} />
        <button className="gac-send" onClick={() => send()} disabled={!input.trim() || streaming}>➤</button>
      </div>
      <p className="gac-disclaimer">{disclaimer}</p>
    </div>
  );
}
